import { useState } from "react";
import {
  RiMailLine,
  RiAddLine,
  RiSearchLine,
  RiFolder2Line,
  RiFolderSharedLine,
  RiStarLine,
  RiStarFill,
  RiDeleteBin6Line,
  RiEditLine,
  RiFileCopyLine,
} from "react-icons/ri";
import AITemplateGenerator from "../components/email/AITemplateGenerator";
import NewTemplateEditor from "../components/email/NewTemplateEditor";

const EmailTemplates = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [showNewEditor, setShowNewEditor] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const categories = [
    { id: "all", name: "All Templates", icon: RiFolder2Line },
    { id: "marketing", name: "Marketing", icon: RiMailLine },
    { id: "newsletters", name: "Newsletters", icon: RiFolderSharedLine },
    { id: "favorites", name: "Favorites", icon: RiStarLine },
  ];

  const templates = [
    {
      id: 1,
      name: "Welcome Email",
      category: "marketing",
      description: "Initial welcome message for new subscribers",
      lastModified: "2023-11-20",
      isFavorite: true,
      stats: {
        opens: 2547,
        clicks: 1123,
        conversions: 342,
      },
      tags: ["welcome", "onboarding"],
    },
    {
      id: 2,
      name: "Monthly Newsletter",
      category: "newsletters",
      description: "Regular monthly updates and news",
      lastModified: "2023-11-18",
      isFavorite: false,
      stats: {
        opens: 1890,
        clicks: 756,
        conversions: 189,
      },
      tags: ["newsletter", "monthly"],
    },
  ];

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" ||
      (selectedCategory === "favorites"
        ? template.isFavorite
        : template.category === selectedCategory);
    return matchesSearch && matchesCategory;
  });

  const handleTemplateAction = (action, template) => {
    switch (action) {
      case "edit":
        setSelectedTemplate(template);

        break;
      case "duplicate":
        break;
      case "delete":
        break;
      case "favorite":
        break;
      default:
        break;
    }
  };

  const handleSaveTemplate = (template) => {
    // Logic to save the template
    console.log("Saving template:", template);
    setShowNewEditor(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">
            Email Templates
          </h1>
          <p className="mt-2 text-secondary-600">
            Create and manage your email templates
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            className="btn btn-secondary flex items-center"
            onClick={() => setShowAIGenerator(true)}
          >
            <RiAddLine className="w-5 h-5 mr-2" />
            AI Generate
          </button>
          <button
            className="btn btn-primary flex items-center"
            onClick={() => setShowNewEditor(true)}
          >
            <RiAddLine className="w-5 h-5 mr-2" />
            New Template
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="lg:w-64 flex-shrink-0">
          <div className="bg-white rounded-lg border border-secondary-200 overflow-hidden">
            <div className="p-4">
              <div className="relative">
                <RiSearchLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  className="pl-10 pr-4 py-2 w-full border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <nav className="border-t border-secondary-200">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`w-full flex items-center px-4 py-3 hover:bg-secondary-50 ${
                    selectedCategory === category.id
                      ? "bg-primary-50 text-primary-600"
                      : "text-secondary-700"
                  }`}
                >
                  <category.icon className="w-5 h-5 mr-3" />
                  <span className="font-medium">{category.name}</span>
                  <span className="ml-auto text-sm text-secondary-500">
                    {category.id === "all"
                      ? templates.length
                      : category.id === "favorites"
                        ? templates.filter((t) => t.isFavorite).length
                        : templates.filter((t) => t.category === category.id)
                            .length}
                  </span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className="bg-white rounded-lg border border-secondary-200 hover:shadow-md transition-shadow"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-secondary-900">
                        {template.name}
                      </h3>
                      <p className="text-sm text-secondary-600 mt-1">
                        {template.description}
                      </p>
                    </div>
                    <button
                      onClick={() => handleTemplateAction("favorite", template)}
                      className={`p-1 rounded-lg hover:bg-secondary-50 ${
                        template.isFavorite
                          ? "text-amber-500"
                          : "text-secondary-400"
                      }`}
                    >
                      {template.isFavorite ? (
                        <RiStarFill className="w-5 h-5" />
                      ) : (
                        <RiStarLine className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  <div className="mt-4 flex items-center text-sm text-secondary-500">
                    <span>Last modified: {template.lastModified}</span>
                    <span className="mx-2">•</span>
                    <span className="capitalize">{template.category}</span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {template.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-secondary-100 text-secondary-700 rounded text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-4 pt-4 border-t border-secondary-200">
                    <div className="flex items-center justify-between">
                      <div className="flex space-x-6 text-sm">
                        <div>
                          <span className="text-secondary-700 font-medium">
                            {template.stats.opens}
                          </span>
                          <span className="text-secondary-500 ml-1">opens</span>
                        </div>
                        <div>
                          <span className="text-secondary-700 font-medium">
                            {template.stats.clicks}
                          </span>
                          <span className="text-secondary-500 ml-1">
                            clicks
                          </span>
                        </div>
                        <div>
                          <span className="text-secondary-700 font-medium">
                            {template.stats.conversions}
                          </span>
                          <span className="text-secondary-500 ml-1">conv.</span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleTemplateAction("edit", template)}
                          className="p-1 text-secondary-400 hover:text-primary-600 rounded-lg hover:bg-secondary-50"
                        >
                          <RiEditLine className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() =>
                            handleTemplateAction("duplicate", template)
                          }
                          className="p-1 text-secondary-400 hover:text-primary-600 rounded-lg hover:bg-secondary-50"
                        >
                          <RiFileCopyLine className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() =>
                            handleTemplateAction("delete", template)
                          }
                          className="p-1 text-secondary-400 hover:text-danger-600 rounded-lg hover:bg-danger-50"
                        >
                          <RiDeleteBin6Line className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI Template Generator Modal */}
      {showAIGenerator && (
        <AITemplateGenerator
          onClose={() => setShowAIGenerator(false)}
          onGenerate={(template) => {
            // Handle the generated template
            setShowAIGenerator(false);
          }}
        />
      )}

      {/* New Template Editor Modal */}
      {showNewEditor && (
        <NewTemplateEditor
          onClose={() => setShowNewEditor(false)}
          onSave={handleSaveTemplate}
        />
      )}
    </div>
  );
};

export default EmailTemplates;
